import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { apiClient } from '../api/client';
import type { UploadResponse } from '../api/types';
import { saveToQueue, getAllPending, removeFromQueue } from '../lib/offlineQueue';
import type { QueueItem } from '../lib/offlineQueue';

export interface UseOfflineQueueReturn {
  pendingCount: number;
  isSyncing: boolean;
  queueRecording: (blob: Blob, mimeType: string, language: string) => Promise<void>;
}

export function useOfflineQueue(): UseOfflineQueueReturn {
  const { t } = useTranslation();
  const { getAccessToken } = useKindeAuth();
  const queryClient = useQueryClient();

  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const isSyncingRef = useRef(false);
  const pendingItemsRef = useRef<QueueItem[]>([]);

  // Load initial pending count and cache items
  useEffect(() => {
    getAllPending().then((items) => {
      pendingItemsRef.current = items;
      setPendingCount(items.length);
    });
  }, []);

  const syncAll = useCallback(async () => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    setIsSyncing(true);

    toast.info(t('offline.syncing'));

    const token = await (getAccessToken as () => Promise<string>)();

    /* istanbul ignore next -- @preserve: fallback for when cache is empty */
    const items =
      pendingItemsRef.current.length > 0 ? pendingItemsRef.current : await getAllPending();
    let anySucceeded = false;

    for (const item of items) {
      try {
        const formData = new FormData();
        const ext = item.mimeType.split('/').pop()!.split(';')[0] || 'webm';
        formData.append('file', item.blob, `recording.${ext}`);

        const uploadResult = await apiClient<UploadResponse>('/upload', {
          method: 'POST',
          body: formData,
          token,
        });
        await apiClient('/process-quote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ file_key: uploadResult.key, language: item.language }),
          token,
        });
        await removeFromQueue(item.id!);
        setPendingCount((prev) => prev - 1);
        toast.success(t('offline.syncSuccess'));
        anySucceeded = true;
      } catch {
        toast.warning(t('offline.syncFailed'));
      }
    }

    // Refresh cached items from IDB
    pendingItemsRef.current = await getAllPending();

    if (anySucceeded) {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
    }

    isSyncingRef.current = false;
    setIsSyncing(false);
  }, [getAccessToken, queryClient, t]);

  // Listen for online event
  useEffect(() => {
    const handler = () => {
      syncAll();
    };
    globalThis.addEventListener('online', handler);
    return () => {
      globalThis.removeEventListener('online', handler);
    };
  }, [syncAll]);

  const queueRecording = useCallback(
    async (blob: Blob, mimeType: string, language: string) => {
      await saveToQueue(blob, mimeType, language);
      pendingItemsRef.current = await getAllPending();
      setPendingCount((prev) => prev + 1);
      toast.info(t('offline.queued'));
    },
    [t],
  );

  return { pendingCount, isSyncing, queueRecording };
}
